import { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNERSHIP_TYPES = [
  { value: "research", label: "Research / Academic" },
  { value: "clinical", label: "Clinical" },
  { value: "investment", label: "Investment" },
  { value: "technical", label: "Technical" },
  { value: "other", label: "Other" },
];

export default function PartnerPage() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    organization: "",
    role: "",
    email: "",
    interest: "research",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setSubmitting(true);
    try {
      const messageWithRole = form.role
        ? `${form.message}\n\n— Role: ${form.role}`
        : form.message;
      await axios.post(`${API}/contact`, {
        name: form.name,
        organization: form.organization,
        email: form.email,
        interest: form.interest,
        message: messageWithRole,
      });
      setDone(true);
      toast.success("Message received — we'll be in touch");
    } catch (err) {
      toast.error("Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-28 md:pt-32 pb-24">
      <header className="max-w-[1180px] mx-auto px-6 lg:px-12">
        <Link
          to="/"
          className="reveal inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft size={12} /> Home
        </Link>
        <div className="reveal mt-6 flex items-center gap-3">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8] nb-pulse" />
            Partner
          </span>
        </div>
        <h1 className="reveal mt-5 font-display text-white text-[clamp(34px,5.2vw,64px)] leading-[1.05] font-medium tracking-tight max-w-3xl headline-glow">
          Partner with Neurobolt.AI
        </h1>
        <p className="reveal mt-5 text-[#CBD5E1] text-[17px] leading-relaxed max-w-2xl">
          We are engaging qualified partners across research, clinical strategy,
          engineering, and investment. Share a short note and we'll follow up if
          there is a fit.
        </p>
      </header>

      <section className="max-w-[1180px] mx-auto px-6 lg:px-12 mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Direct contact */}
        <aside className="lg:col-span-4">
          <div className="reveal glass rounded-3xl p-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#38BDF8]">
              Direct email
            </div>
            <a
              href="mailto:contact@neurobolt.ai"
              className="mt-4 inline-flex items-center gap-2 font-display text-white text-[22px] leading-tight link-underline"
            >
              <Mail size={16} className="text-[#38BDF8]" />
              contact@neurobolt.ai
            </a>
            <p className="mt-5 text-[13px] text-[#94A3B8] leading-relaxed">
              For confidential outreach, please send a brief introduction and
              the area you'd like to discuss. We respond to qualified inquiries
              within a few business days.
            </p>

            <div className="mt-6 border-t border-white/10 pt-5 space-y-3">
              <Pill label="Research / Academic" />
              <Pill label="Clinical strategy" />
              <Pill label="Investment" />
              <Pill label="Technical collaboration" />
            </div>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-8">
          {done ? (
            <div className="reveal glass rounded-3xl p-10 text-center">
              <h2 className="font-display text-white text-[26px] font-medium">
                Message received.
              </h2>
              <p className="mt-3 text-[#94A3B8] text-[14.5px]">
                Thanks — we'll follow up from contact@neurobolt.ai if there's a fit.
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-[13px] hover:border-[#38BDF8]/50"
              >
                <ArrowLeft size={14} /> Back to home
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="reveal glass rounded-3xl p-7 lg:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name">
                  <Input
                    data-testid="partner-name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email">
                  <Input
                    data-testid="partner-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                    placeholder="you@org.com"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Organization">
                  <Input
                    data-testid="partner-org"
                    value={form.organization}
                    onChange={update("organization")}
                    className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                    placeholder="Institute / Company"
                  />
                </Field>
                <Field label="Role">
                  <Input
                    data-testid="partner-role"
                    value={form.role}
                    onChange={update("role")}
                    className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                    placeholder="Professor, Investor, Engineer…"
                  />
                </Field>
              </div>
              <Field label="Partnership type">
                <Select
                  value={form.interest}
                  onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                >
                  <SelectTrigger
                    data-testid="partner-interest"
                    className="bg-[#0B1426] border-white/10 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#07111F] border-white/10 text-white">
                    {PARTNERSHIP_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message">
                <Textarea
                  data-testid="partner-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white resize-none"
                  placeholder="Brief intro and how you'd like to engage…"
                />
              </Field>
              <button
                data-testid="partner-submit"
                type="submit"
                disabled={submitting}
                className="btn-glow w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-white text-[#020617] text-[14px] font-semibold hover:bg-[#BFDBFE] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send message <ArrowRight size={14} />
                  </>
                )}
              </button>
              <p className="text-[11.5px] text-[#64748B] text-center">
                By submitting, you agree we may reply by email. We don't share
                inbound notes.
              </p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#94A3B8]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Pill({ label }) {
  return (
    <div className="flex items-center gap-3 text-[12.5px] text-[#CBD5E1]">
      <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8]" />
      {label}
    </div>
  );
}
